import Select, { OptionProps } from '@storeblocks/select';
import { nanoid } from 'nanoid';
import { type FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { AddSingleItemContext } from './context';
import { Item } from './Item';
import { RemoveButton } from './RemoveButton';

interface Props {
  placeholderText: string;

  /**
   * @param previousItemId
   * The value is `undefined` for the initial selected item.
   */
  onChange: (newItem: Item, previousItemId: string | undefined) => void;

  /**
   * @param selectedItem
   * The value is `undefined` if an item has not been selected.
   */
  onRemoveClick: (selectedItemId: string | undefined) => void;
}

export const SelectListRow: FC<Props> = ({
  placeholderText,
  onChange: onSelectChange,
  onRemoveClick,
}) => {
  const [selectedItem, setSelectedItem] = useState<{
    option: OptionProps;
    index: number;
  }>();
  const [options, setOptions] = useState<OptionProps[]>([]);
  const [htmlId] = useState(nanoid());
  const context = useContext(AddSingleItemContext);

  useEffect(() => {
    // Create options for the select list.
    const selectListItems: OptionProps[] = context.selectableItems.map(
      (item) => ({
        label: item.label,
        value: item.id,
      }),
    );

    // The selectable items list from the context does not contain the selected item
    // since it is not supposed to be selected by the other select lists.
    // Thus we have to add it back to the current select list.
    if (selectedItem) {
      selectListItems.splice(selectedItem.index, 0, selectedItem.option);
    }

    setOptions(selectListItems);
  }, [context.selectableItems]);

  const onChange = (option: OptionProps | undefined): void => {
    if (!option) {
      return;
    }

    const index = context.selectableItems.findIndex(
      (item) => item.id === option.value,
    );
    const newItem = context.selectableItems[index];

    onSelectChange(newItem, selectedItem?.option.value);

    setSelectedItem({
      option,
      index,
    });
  };

  return (
    <Wrapper>
      <StyledSelect
        id={htmlId}
        value={selectedItem?.option.value || ''}
        name={`select-item-${htmlId}`}
        placeholder={placeholderText}
        options={options}
        onChange={(_, option) => onChange(option)}
        fluid
        label=""
      />
      <RemoveButton onClick={() => onRemoveClick(selectedItem?.option.value)} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
`;

const StyledSelect = styled(Select)`
  // The Select component has a label that we are not using, it is currently set to an empty string.
  // Remove the gap on the container holding the label and the select, to ensure the select aligns nicely in the row.
  &.selectBase {
    gap: 0;
  }
`;
